import React, { useMemo, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
	Drawer,
	TextField,
	FormControl,
	FormGroup,
	FormControlLabel,
	Checkbox,
	Button,
} from "@mui/material";

// Utility function to get label for layout items
const getLayoutItemLabel = (itemId) => {
	const labelMap = {
		totalTransactionAmount: "Total Transaction",
		totalOnlineTransaction: "Online Transactions",
		totalOfflineTransaction: "Offline Transaction",
		totalSuccessfulTransactions: "Successful Transactions",
		totalPendingTransactions: "Pending Transactions",
		totalRefundTransactions: "Refund Transactions",
		totalFailedTransactions: "Failed Transactions",
		totalTransactionCountChart: "Total Transaction Count",
		transactionActivityChart: "Transaction Activity",
		revenueMerchantChart: "Revenue by Tenant",
		paymentDistributionChart: "Payout Distribution",
		recentTransactionList: "Recent Transactions",
		topPerformingMerchantList: "Top Performing Merchants",
	};
	return labelMap[itemId] || itemId;
};

// Utility function to get label for customise sidebar
const getCustomiseLayoutItemLabel = (itemId) => {
	const labelMap = {
		totalTransactionAmount: "Account Balance",
		totalSuccessfulTransactions: "Total Transactions",
		totalPendingTransactions: "Pending Transactions",
		totalRefundTransactions: "Refund Transactions",
		totalFailedTransactions: "Pending Payouts",
		totalOnlineTransaction: "Total Refunds",
		totalOfflineTransaction: "Average Transaction",
		totalTransactionCountChart: "Total Transaction Count",
		transactionActivityChart: "Transaction Activity",
		revenueMerchantChart: "Revenue by Tenant",
		paymentDistributionChart: "Payout Distribution",
		recentTransactionList: "Recent Transactions",
		topPerformingMerchantList: "Top Performing Merchants",
	};
	return labelMap[itemId] || itemId;
};

// Helper to update layout config properties
const updateLayoutConfig = (config, { static: isStatic, isResizable }) => ({
	...config,
	static: isStatic,
	isResizable,
});

export const DashboardLayoutSidebar = ({
	layoutSlideIsOpen,
	setLayoutSlideIsOpen,
	defaultLayout,
	getValues,
	setGetValues,
	handleAddLayout,
	setLayout,
}) => {
	const formik = useFormik({
		initialValues: {
			layoutName: "",
			layoutsConfig: defaultLayout,
		},
		validationSchema: Yup.object({
			layoutName: Yup.string().required("Please enter the Layout Name."),
		}),
		onSubmit: async (values) => {
			const submissionData = {
				...values,
				layoutsConfig: getValues,
			};
			await handleAddLayout(submissionData);
			formik.resetForm();
			const updatedConfig = getValues.map((config) =>
				updateLayoutConfig(config, { static: true, isResizable: false })
			);
			setGetValues(updatedConfig);
		},
	});

	const handleCheckboxChange = useCallback(
		(event) => {
			const { checked } = event.target;
			const itemObject = JSON.parse(event.target.value);
			// In create layout mode (customization), components should NOT be resizable
			const updatedItem = {
				...itemObject,
				static: true,
				isResizable: false,
			};

			const removeAndAddElements = checked
				? [...getValues, updatedItem]
				: getValues.filter((item) => item.i !== itemObject.i);

			formik.setFieldValue("layoutsConfig", removeAndAddElements);
			setGetValues(removeAndAddElements);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[getValues, setGetValues,formik]
	);

	return (
		<Drawer
			id="create-layout"
			variant="persistent"
			anchor="right"
			open={layoutSlideIsOpen}
			className="sidebar-drawer"
			onClose={() => {}}
			ModalProps={{
				disableEnforceFocus: true,
				disablePortal: true,
				onBackdropClick: () => {},
			}}
		>
			<div className="sidebar-header">
				<h2 className="sidebar-header-title">Create Custom Dashboard</h2>
				<p className="sidebar-header-subtitle">
					Design your personalized dashboard layout
				</p>
			</div>

			<form onSubmit={formik.handleSubmit} className="sidebar-form">
				<div className="sidebar-content">
					<div className="sidebar-field">
						<label className="sidebar-field-label">
							Layout Name *
						</label>
						<TextField
							fullWidth
							name="layoutName"
							placeholder="Enter layout name (e.g., Sales Dashboard)"
							value={formik.values.layoutName}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.layoutName && Boolean(formik.errors.layoutName)}
							helperText={formik.touched.layoutName && formik.errors.layoutName}
							size="small"
						/>
					</div>

					<div className="sidebar-components-section">
						<label className="sidebar-components-label">
							Select Dashboard Components
						</label>
						<FormControl fullWidth>
							<FormGroup className="sidebar-checkbox-group">
								{defaultLayout.map((item) => (
									<FormControlLabel
										className="sidebar-checkbox-item"
										key={item.i}
										control={
											<Checkbox
												size="small"
												value={JSON.stringify(item)}
												checked={getValues.some(
													(selected) => selected.i === item.i
												)}
												onChange={handleCheckboxChange}
											/>
										}
										label={getLayoutItemLabel(item.i)}
									/>
								))}
							</FormGroup>
						</FormControl>
					</div>
				</div>

				<div className="sidebar-footer">
					<div className="sidebar-button-group">
						<Button variant="contained" color="primary" type="submit" fullWidth>
							Create Layout
						</Button>

						<div className="sidebar-button-row">
							<Button
								variant="outlined"
								color="primary"
								onClick={() => {
									setLayoutSlideIsOpen(!layoutSlideIsOpen);
									const updatedConfig = defaultLayout.map(
										(elem) =>
											updateLayoutConfig(elem, {
												static: false,
												isResizable: true,
											})
									);
									setGetValues(updatedConfig);
								}}
							>
								Edit Existing
							</Button>
							<Button
								variant="outlined"
								color="error"
								onClick={() => {
									setLayout("default");
									setLayoutSlideIsOpen(!layoutSlideIsOpen);
									formik.resetForm();
									const updatedConfig = defaultLayout.map(
										(elem) =>
											updateLayoutConfig(elem, {
												static: true,
												isResizable: false,
											})
									);
									setGetValues(updatedConfig);
								}}
							>
								Cancel
							</Button>
						</div>
					</div>
				</div>
			</form>
		</Drawer>
	);
};

export const DashboardLayoutSidebarForCustomise = ({
	layoutSlideUpdateIsOpen,
	setLayoutSlideUpdateIsOpen,
	defaultLayout,
	getValues,
	setGetValues,
	handleUpdateLayout,
	handleDeleteLayout,
	handleCancelLayoutUpdate,
	getDataForDashboard,
	layout,
	setIsDisableLayoutSelect,
	setFloatMenu,
	setIsEditMode,
	isEditMode,
}) => {
	const getAllValues = useMemo(
		() => getDataForDashboard?.find((item) => item.id === layout),
		[getDataForDashboard, layout]
	);

	const initialValues = useMemo(
		() => ({
			layoutName: getAllValues?.layoutName || "",
			layoutsConfig: getAllValues?.layoutsConfig
				? Array.isArray(getAllValues.layoutsConfig)
					? getAllValues.layoutsConfig
					: JSON.parse(getAllValues.layoutsConfig)
				: [],
		}),
		[getAllValues]
	);

	const formik = useFormik({
		initialValues,
		enableReinitialize: true,
		validationSchema: Yup.object({
			layoutName: Yup.string().required("Layout name is required"),
		}),
		onSubmit: async (values) => {
			const submissionData = {
				...values,
				layoutsConfig: getValues,
			};
			await handleUpdateLayout(submissionData, getAllValues?.id);
			const updatedConfig = getValues.map((config) =>
				updateLayoutConfig(config, { static: true, isResizable: false })
			);
			setGetValues(updatedConfig);
			setIsDisableLayoutSelect(false);
		},
	});

	const handleCheckboxChange = useCallback(
		(event) => {
			const { checked } = event.target;
			const itemObject = JSON.parse(event.target.value);
			const updatedItem = {
				...itemObject,
				static: false,
				isResizable: true,
			};

			const removeAndAddElements = checked
				? [...getValues, updatedItem]
				: getValues.filter((item) => item.i !== itemObject.i);

			formik.setFieldValue("layoutsConfig", removeAndAddElements);
			setGetValues(removeAndAddElements);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[getValues, setGetValues,formik]
	);

	return (
		<Drawer
			id="edit-layout"
			anchor="right"
			open={layoutSlideUpdateIsOpen}
			variant="persistent"
			className="sidebar-drawer"
			onClose={() => {}}
			ModalProps={{
				disablePortal: true,
				onBackdropClick: () => {},
			}}
		>
			<div className="sidebar-header">
				<h2 className="sidebar-header-title">Edit Dashboard Layout</h2>
				<p className="sidebar-header-subtitle">
					Customize your dashboard components
				</p>
			</div>

			<form onSubmit={formik.handleSubmit} className="sidebar-form">
				<div className="sidebar-content">
					<div className="sidebar-field">
						<label className="sidebar-field-label">
							Layout Name *
						</label>
						<TextField
							fullWidth
							name="layoutName"
							placeholder="Enter layout name"
							value={formik.values.layoutName}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={
								formik.touched.layoutName &&
								Boolean(formik.errors.layoutName)
							}
							helperText={
								formik.touched.layoutName &&
								formik.errors.layoutName
							}
							size="small"
						/>
					</div>

					<div className="sidebar-components-section">
						<label className="sidebar-components-label">
							Dashboard Components
						</label>
						<FormControl fullWidth>
							<FormGroup className="sidebar-checkbox-group">
								{defaultLayout.map((item) => (
									<FormControlLabel
										className="sidebar-checkbox-item mx-0"
										key={item.i}
										control={
											<Checkbox
												size="small"
												value={JSON.stringify(item)}
												checked={getValues.some(
													(selected) =>
														selected.i === item.i
												)}
												onChange={handleCheckboxChange}
											/>
										}
										label={getCustomiseLayoutItemLabel(
											item.i
										)}
									/>
								))}
							</FormGroup>
						</FormControl>
					</div>
				</div>

				<div className="sidebar-footer">
					<div className="sidebar-button-group">
						<div className="sidebar-button-row">
							<Button
								variant="contained"
								color="primary"
								type="submit"
							>
								Update Layout
							</Button>
							<Button
								variant="contained"
								color="error"
								onClick={() => {
									handleDeleteLayout(getAllValues?.id);
									setIsDisableLayoutSelect(false);
								}}
							>
								Delete Layout
							</Button>
						</div>

						<div className="sidebar-button-row">
							<Button
								variant="outlined"
								color="primary"
								disabled={isEditMode}
								onClick={() => {
									// Enable edit mode - make components draggable and resizable
									setIsEditMode(true);
									const editableConfig = getValues.map((item) => ({
										...item,
										static: false,
										isResizable: true,
									}));
									setGetValues(editableConfig);
									
									// Close sidebar while staying in edit mode
									setLayoutSlideUpdateIsOpen(false);
									// Also close the float menu (Dashboard Layout popup)
									setFloatMenu(false);
								}}
							>
								{isEditMode ? "EDITING..." : "Edit Mode"}
							</Button>
							<Button
								variant="outlined"
								color="error"
								onClick={() => {
									handleCancelLayoutUpdate(getAllValues);
								}}
							>
								Cancel
							</Button>
						</div>
					</div>
				</div>
			</form>
		</Drawer>
	);
};
